import { AbstractMesh, InstancedMesh, Mesh, PBRMaterial } from '@babylonjs/core';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { clearArchetype } from '@modules/standard';
import { World } from 'miniplex';

import { LayerMask } from '../../Constants';
import { LightManager } from '../../LightManager';
import { MeshType } from '../component/data/DanjiMesh';
import { Entity } from '../Entity';

export enum AlphaLayer {
  DEFAULT = 0,
  DANJI_BUILDING_WALL = 1,
  DANJI_SUPPLEMENTARY = 1.5,
  DANJI_BUILDING_EFFECT = 2,
  DANJI_ITEM_EFFECT = 3,
  DANJI_UNIT_PANEL = 4,
}

type MeshSettings = {
  receiveMainLight: boolean;
  receiveSubLight: boolean;
  receiveShadows: boolean;
  castShadow: boolean;
  isPickable: boolean;
  checkCollisions: boolean;
  alwaysSelectAsActiveMesh: boolean;
  doNotSyncBoundingInfo: boolean;
  freezeMesh: boolean;
  maxSimultaneousLights: number;
  alphaLayer: AlphaLayer;
  layerMask: number;
};
const defaultMeshSettings: MeshSettings = {
  receiveMainLight: true,
  receiveSubLight: true,
  receiveShadows: false,
  castShadow: false,
  isPickable: false,
  checkCollisions: false,
  alwaysSelectAsActiveMesh: false,
  doNotSyncBoundingInfo: true,
  freezeMesh: true,
  maxSimultaneousLights: 2,
  alphaLayer: AlphaLayer.DEFAULT,
  layerMask: LayerMask.Default,
};

/**
 * 설명 추가 필요
 *
 * @group System
 * @category System
 */
export class DanjiMeshSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('DanjiMeshSystem');

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------
  private readonly targetArchetype;

  private readonly lightManager;

  //-----------------------
  constructor({
    [Zed.WORLD_TOKEN]: world,
    [LightManager.INJECTABLE_TOKEN]: lightManager,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [LightManager.INJECTABLE_TOKEN]: LightManager;
  }) {
    this.lightManager = lightManager;

    this.targetArchetype = world.with('DanjiMeshes');
    this.targetArchetype.onEntityAdded.subscribe(this.initializeDanjiMeshes);
  }

  private initializeDanjiMeshes = (entity: (typeof this.targetArchetype.entities)[number]) => {
    entity.DanjiMeshes.danjiMeshes.forEach((danjiMeshes) => {
      switch (danjiMeshes.type) {
        case MeshType.GIS_GROUND:
          this.initializeMeshes(danjiMeshes.meshes, {
            receiveShadows: true,
            isPickable: true,
            checkCollisions: true,
            layerMask: LayerMask.Terrain,
          });
          break;
        case MeshType.DECAL_GRASS:
        case MeshType.DECAL_CAR_ROAD:
        case MeshType.DECAL_WALK_ROAD:
        case MeshType.DECAL_EDGE:
        case MeshType.DECAL_PLAYGROUND:
        case MeshType.DECAL_GYM:
        case MeshType.DECAL_RECYCLE:
        case MeshType.DECAL_WATER:
          this.initializeMeshes(danjiMeshes.meshes, {
            receiveShadows: true,
            layerMask: LayerMask.Decal,
          });
          break;
        case MeshType.DONG_PROP:
          this.initializeMeshes(danjiMeshes.meshes, {
            layerMask: LayerMask.Complex | LayerMask.Building,
          });
          break;
        case MeshType.DONG_ROOF_TOP:
          this.initializeMeshes(danjiMeshes.meshes, {
            layerMask: LayerMask.Complex | LayerMask.Building | LayerMask.Rooftop,
          });
          break;
        case MeshType.DONG_ITEM_HO:
          this.initializeMeshes(danjiMeshes.meshes, {
            receiveMainLight: false,
            receiveSubLight: false,
            alphaLayer: AlphaLayer.DANJI_ITEM_EFFECT,
            layerMask: LayerMask.Complex | LayerMask.Building | LayerMask.Effect,
          });
          break;
        case MeshType.GIS_BUILDING:
          this.initializeMeshes(danjiMeshes.meshes, {
            receiveShadows: true,
            castShadow: true,
            isPickable: false,
            checkCollisions: false,
            alphaLayer: AlphaLayer.DANJI_SUPPLEMENTARY,
            layerMask: LayerMask.Building,
          });
          break;
        case MeshType.DONG_FRAME:
          this.initializeMeshes(danjiMeshes.meshes, {
            receiveShadows: true,
            castShadow: true,
            isPickable: true,
            checkCollisions: false,
            alphaLayer: AlphaLayer.DANJI_BUILDING_WALL,
            layerMask: LayerMask.Complex | LayerMask.Building | LayerMask.Effect,
          });
          break;
        case MeshType.DONG_ROOMTYPE_WALL:
          this.initializeMeshes(danjiMeshes.meshes, {
            receiveShadows: true,
            receiveMainLight: true,
            receiveSubLight: true,
            alphaLayer: AlphaLayer.DANJI_BUILDING_WALL,
            layerMask: LayerMask.Complex | LayerMask.Effect,
          });
          break;
        case MeshType.FACILITY_TREE_NORMAL:
        case MeshType.FACILITY_TREE_BUSH:
        case MeshType.FACILITY_TREE_FLOWER:
        case MeshType.FACILITY_PARKING_ENTERANCE:
          this.initializeMeshes(danjiMeshes.meshes, {
            receiveSubLight: false,
            alphaLayer: AlphaLayer.DANJI_SUPPLEMENTARY,
            layerMask: LayerMask.Facility,
          });
          break;
        case MeshType.FACILITY_GYM:
        case MeshType.FACILITY_PARKING:
          this.initializeMeshes(danjiMeshes.meshes, {
            receiveShadows: true,
            layerMask: LayerMask.Facility | LayerMask.Decal,
          });
          break;
        case MeshType.FACILITY_BUILDING:
          this.initializeMeshes(danjiMeshes.meshes, {
            receiveShadows: true,
            isPickable: true,
            layerMask: LayerMask.Facility | LayerMask.Building,
          });
          break;
      }
    });
  };

  private initializeMeshes(meshes: Mesh[] | AbstractMesh[] | InstancedMesh[], params?: Partial<MeshSettings>) {
    const settings = { ...defaultMeshSettings, ...params };

    const uniqueMeshes = new Set(meshes.map((mesh) => (mesh instanceof InstancedMesh ? mesh.sourceMesh : mesh)));

    uniqueMeshes.forEach((mesh) => {
      mesh.receiveShadows = settings.receiveShadows;
      mesh.isPickable = settings.isPickable;
      mesh.checkCollisions = settings.checkCollisions;

      mesh.alwaysSelectAsActiveMesh = settings.alwaysSelectAsActiveMesh;
      mesh.doNotSyncBoundingInfo = settings.doNotSyncBoundingInfo;
      mesh.alphaIndex = settings.alphaLayer;
      if (settings.receiveMainLight) this.lightManager.addMainLightRenderList(mesh);
      if (settings.castShadow) {
        this.lightManager.addShadowCaster(mesh);
        this.lightManager.addShadowCaster(mesh, true);
      }

      mesh.layerMask = settings.layerMask;

      if (settings.freezeMesh) {
        mesh.computeWorldMatrix();
        mesh.freezeWorldMatrix();
        if (mesh instanceof Mesh) mesh.freezeNormals();
      }

      if (mesh.material && mesh.material instanceof PBRMaterial) {
        mesh.material.maxSimultaneousLights = settings.maxSimultaneousLights;
      }
    });
  }

  dispose() {
    this.targetArchetype.entities.forEach((e) => {
      e.DanjiMeshes.danjiMeshes.forEach((dm) => {
        dm.meshes.forEach((m) => m.dispose(false, true));
      });
    });
    clearArchetype(this.targetArchetype);
  }

  public setVisible(type: MeshType, isVisible: boolean) {
    this.targetArchetype.entities.forEach((e) => {
      e.DanjiMeshes.danjiMeshes.forEach((dm) => {
        if (dm.type === type)
          dm.meshes.forEach((m) => {
            m.isVisible = isVisible;
            if (isVisible && !m.isEnabled()) {
              m.setEnabled(isVisible);
            }
          });
      });
    });
  }

  // --------------------------------
  readonly operations: SystemOperationDefinition[] = [];
}
